import React, { useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import Layout from '../../../components/layout/Layout';
import { UserPlus, Save, Check, DollarSign, Star, Receipt } from 'lucide-react';
import { clientsApi } from '../../../api/clientsApi';
import { useError } from '../../../context/ErrorContext';

// --- Constantes fuera del componente para mejor rendimiento ---
// Catálogo compartido (src/lib/countries.ts). Los países más allá de MX/US solo
// aparecen con el flag intl_phones del negocio; la forma que se guarda la decide
// storedPhoneFor (MX/US = 10 locales; resto = lada+nacional).
import { countriesFor, countryByCode, storedPhoneFor } from '../../../lib/countries';

const MONTHS = [
  'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
  'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
];

const RegisterClientPage: React.FC = () => {
  const { currentUser } = useAuth();
  const { handleFirebaseError, clearErrors, addValidationError } = useError();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState('MX');

  // ✅ Detección de Modo de Lealtad
  const loyaltyMode = currentUser?.loyaltyMode || 'visits'; // 'visits' | 'cashback'

  // Form fields
  const [phoneNumber, setPhoneNumber] = useState('');
  const [countryCode, setCountryCode] = useState('+52');
  const [visitorName, setVisitorName] = useState('');

  const COUNTRIES = countriesFor(currentUser?.intlPhones);
  const country = countryByCode(selectedCountry) || COUNTRIES[0];
  const natDigits = country.nationalDigits;

  const handleCountryChange = (code: string) => {
    setSelectedCountry(code);
    setCountryCode(countryByCode(code)?.prefix || '+52');
  };

  const validatePhoneNumber = (number: string) => {
    const digits = number.replace(/\D/g, '');
    return digits.length === natDigits;
  };

  const [email, setEmail] = useState('');
  const [gender, setGender] = useState('');
  const [birthDay, setBirthDay] = useState('');
  const [birthMonth, setBirthMonth] = useState('');
  const [initialPoints, setInitialPoints] = useState('');
  // 'purchase' = registrar su PRIMERA COMPRA/VISITA (check-in real: % de cashback automático,
  // cuenta la visita y dispara la bienvenida con su saldo). 'gift' = saldo de regalo directo.
  const [initialMode, setInitialMode] = useState<'purchase' | 'gift'>('purchase');
  // Folio del ticket (24-oct-2026, Grupo Cenizas del Sol): con el folio en «obligatorio», la primera compra
  // va al mismo /loyalty/checkin que el registro de visita y el servidor la rechazaba (400 TICKET_REF_REQUIRED)
  // porque esta pantalla no tenía dónde capturarlo; el panel lo enseñaba como «error inesperado».
  const modoFolio = currentUser?.ticketRef || 'off';
  const [folio, setFolio] = useState('');
  const pideFolio = initialMode === 'purchase' && loyaltyMode === 'cashback' && modoFolio !== 'off';

  // «¿Quién lo recomendó?» (24-oct-2026, Hogar China City: «añadí a uno en mi compu y no me apareció»).
  // Solo con referidos encendidos. Acepta el teléfono del cliente que recomendó o su código.
  const pideReferido = !!currentUser?.referralsEnabled;
  const [referido, setReferido] = useState('');
  const [avisoReferido, setAvisoReferido] = useState('');

  // Business configuration
  const showGender = currentUser?.requestGender || false;
  const showBirthday = currentUser?.requestBirthday || false;
  const showEmail = currentUser?.requestEmail || false;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!currentUser?.businessId) return;

    try {
      setLoading(true);
      clearErrors();
      setSuccess(false);

      const cleanPhone = phoneNumber.trim();
      const cleanCountryCode = countryCode.trim();

      // 1. Validaciones básicas del formulario (largo según el país elegido)
      if (!cleanPhone || cleanPhone.length !== natDigits) {
        addValidationError(`El número de ${country.name} debe tener exactamente ${natDigits} dígitos`, 'phoneNumber');
        setLoading(false); 
        return;
      }
      // Forma canónica que guarda base: MX/US 10 locales; otro país lada+nacional.
      const storedPhone = storedPhoneFor(country.code, cleanPhone);

      if (!visitorName.trim()) {
        addValidationError('El nombre es requerido', 'visitorName');
        setLoading(false);
        return;
      }

      if (showEmail && email.trim() && !email.includes('@')) {
        addValidationError('El correo electrónico no es válido', 'email');
        setLoading(false);
        return;
      }

      if (showBirthday && (birthDay || birthMonth)) {
        const day = parseInt(birthDay);
        const month = parseInt(birthMonth);
        if (!birthDay || isNaN(day) || day < 1 || day > 31) {
          addValidationError('El día de nacimiento no es válido', 'birthDay');
          setLoading(false);
          return;
        }
        // ✅ CORREGIDO: Validación 0-11 (consistente con App.tsx)
        if (birthMonth === '' || isNaN(month) || month < 0 || month > 11) {
          addValidationError('El mes de nacimiento no es válido', 'birthMonth');
          setLoading(false);
          return;
        }
      }

      // ✅ Lógica de puntos corregida para aceptar decimales en modo Cashback
      const rawPoints = initialPoints === '' ? 0 : parseFloat(initialPoints);
      
      if (isNaN(rawPoints) || rawPoints < 0) {
        addValidationError(
            loyaltyMode === 'cashback' 
            ? 'El saldo inicial debe ser un número positivo' 
            : 'Los puntos iniciales deben ser un número entero positivo', 
            'initialPoints'
        );
        setLoading(false);
        return;
      }

      // Si es 'visits', forzamos entero por seguridad visual
      const finalPoints = loyaltyMode === 'visits' ? Math.floor(rawPoints) : parseFloat(rawPoints.toFixed(2));

      // ===========================================================
      // 2. VERIFICACIÓN DE DUPLICADOS (pre-check contra lealify-base)
      // ===========================================================
      const fullPhoneNumber = `${cleanCountryCode} ${cleanPhone}`;
      const existing = await clientsApi.lookup(storedPhone);
      if (existing) {
        addValidationError(
          `El número ${fullPhoneNumber} ya pertenece a ${existing.visitorName}`,
          'phoneNumber'
        );
        setLoading(false);
        return;
      }
      // ===========================================================

      const hasBirthday = !!(showBirthday && birthDay && birthMonth !== '');

      // Referidor: se valida ANTES de crear al cliente, para no dar de alta a medias.
      let refPhone: string | undefined, refCode: string | undefined, refNombre = '';
      const refRaw = pideReferido ? referido.trim() : '';
      if (refRaw) {
        const dig = refRaw.replace(/\D/g, '');
        if (/^[0-9\s()+-]+$/.test(refRaw) && dig.length >= 10) {
          // MX/US: los 10 locales aunque lo escriban con 52/521/1 delante.
          let nat = dig;
          if (nat.length === 13 && nat.startsWith('521')) nat = nat.slice(3);
          else if (nat.length === 12 && nat.startsWith('52')) nat = nat.slice(2);
          else if (nat.length === 11 && nat.startsWith('1')) nat = nat.slice(1);
          if (nat === storedPhone) { addValidationError('El cliente no puede recomendarse a sí mismo', 'referido'); setLoading(false); return; }
          const quien = await clientsApi.lookup(nat);
          if (!quien) { addValidationError(`No hay ningún cliente con el número ${nat} para registrarlo como quien recomendó`, 'referido'); setLoading(false); return; }
          refPhone = nat; refNombre = quien.visitorName;
        } else {
          const r = await clientsApi.resolveReferralCode(refRaw.toUpperCase());
          if (!r.found) { addValidationError(`No encontramos el código «${refRaw.toUpperCase()}»`, 'referido'); setLoading(false); return; }
          refCode = refRaw.toUpperCase(); refNombre = r.contact?.first_name || '';
        }
      }
      let referralRes: { ok: boolean; reason?: string } | undefined;

      if (pideFolio && modoFolio === 'required' && finalPoints > 0 && !folio.trim()) {
        addValidationError('Captura el folio del ticket de esa compra', 'folio');
        setLoading(false);
        return;
      }

      if (initialMode === 'purchase' && (loyaltyMode === 'visits' || finalPoints > 0)) {
        // 3a. PRIMERA COMPRA/VISITA → check-in real (mismo motor que el kiosko): crea el
        //     contacto, registra la visita (Últimas Visitas/Inactivos), aplica el % de
        //     cashback automático (finalPoints = monto de VENTA aquí) y dispara la
        //     bienvenida con su saldo. birth_month va 0-indexado (paridad kiosko).
        const out = await clientsApi.checkin({
          phone: storedPhone,
          firstName: visitorName.trim(),
          amount: loyaltyMode === 'cashback' ? finalPoints : 0,
          gender: showGender && gender ? gender : undefined,
          email: showEmail && email.trim() ? email.trim() : undefined,
          birthDay: hasBirthday ? parseInt(birthDay) : undefined,
          birthMonth0: hasBirthday ? parseInt(birthMonth) : undefined,
          ticketRef: pideFolio && folio.trim() ? folio.trim() : undefined,
          referredByPhone: refPhone, referredByCode: refCode,
        });
        if (refPhone || refCode) referralRes = out.referral || { ok: false, reason: 'error' };
      } else {
        // 3b. Alta sin venta (o con saldo de REGALO directo): crear contacto + ajuste.
        //     La bienvenida sale en el primer movimiento (fix 2026-07-04 en /loyalty/adjust).
        const { id } = await clientsApi.create({
          visitorName: visitorName.trim(),
          phoneNumber: storedPhone,
          email: showEmail && email.trim() ? email.trim() : undefined,
          birthDay: hasBirthday ? parseInt(birthDay) : undefined,
          birthMonth: hasBirthday ? parseInt(birthMonth) + 1 : undefined,
        });
        // DECISIÓN (20-ago-2026, Carlos): este saldo inicial de regalo NO se gatea con PRO,
        // aunque "Gestionar Puntos" sí lo esté. No es un descuido: medido en la base, negocios
        // sin plan de pago lo usan de verdad (5 negocios, el último el 18-ago) y cerrarlo les
        // quitaría algo que hoy funciona. La regla queda: en gratis se puede dar saldo AL DAR
        // DE ALTA; cargar puntos después es del plan PRO.
        if (refPhone || refCode) referralRes = await clientsApi.addReferral({ referredContactId: id, referrerPhone: refPhone, referrerCode: refCode });
        if (initialMode === 'gift' && finalPoints > 0) {
          await clientsApi.adjust({
            contactId: id,
            points: finalPoints,
            note: 'Saldo inicial de regalo (alta manual)',
          });
        }
      }

      if (referralRes) {
        const motivos: Record<string, string> = {
          referrals_disabled: 'los referidos están apagados', self_referral: 'no puede recomendarse a sí mismo',
          referrer_not_found: 'no encontramos a quien recomendó', daily_cap: 'quien recomendó ya llegó al tope de hoy',
          already_referred: 'ya tenía un referido registrado',
        };
        setAvisoReferido(referralRes.ok
          ? `Referido registrado: ${refNombre || 'quien recomendó'} recibió sus puntos por recomendarlo.`
          : `El cliente quedó registrado, pero el referido no se aplicó (${motivos[referralRes.reason || ''] || referralRes.reason || 'error'}).`);
      } else setAvisoReferido('');

      // Show success message
      setSuccess(true);

      // Reset form
      setPhoneNumber('');
      setVisitorName('');
      setEmail('');
      setGender('');
      setBirthDay('');
      setBirthMonth('');
      setInitialPoints('');
      setFolio('');
      setReferido('');

      setTimeout(() => {
        setSuccess(false);
      }, 3000);

    } catch (err) {
      // Un rechazo de negocio del servidor (400/409: folio obligatorio, sucursal, ventana…) se enseña con SU texto;
      // antes todo caía en «Ha ocurrido un error inesperado» y el operador no sabía qué corregir.
      const e = err as { status?: number; data?: { error?: string } };
      if (e && typeof e.status === 'number' && e.status >= 400 && e.status < 500 && e.data?.error) {
        addValidationError(e.data.error, 'initialPoints');
      } else {
        handleFirebaseError(err, 'registerClient');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="space-y-6">
        <div className="animate-fade-in-up" style={{animationDelay: '0.1s'}}>
          <h1 className="text-2xl font-bold text-content flex items-center">
            <UserPlus className="h-6 w-6 mr-2 text-accent" /> 
            Registrar Nuevo Cliente
          </h1>
          <p className="mt-1 text-sm text-content-2">
            Ingresa los datos del nuevo cliente manualmente
          </p>
        </div>

        {success && (
          <div className="bg-green-50 dark:bg-green-500/10 border-l-4 border-green-400 p-4 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
            <div className="flex">
              <div className="flex-shrink-0">
                <Check className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3">
                <p className="text-sm text-green-700 dark:text-green-300">
                  Cliente registrado exitosamente
                </p>
                {avisoReferido && <p className="text-sm text-green-700 dark:text-green-300 mt-1" data-testid="alta-referido-aviso">{avisoReferido}</p>}
              </div>
            </div>
          </div>
        )}

        <div className="bg-surface shadow rounded-lg overflow-hidden animate-fade-in-up" style={{animationDelay: '0.15s'}}>
          <form onSubmit={handleSubmit} className="divide-y divide-line">
            <div className="px-4 py-5 sm:p-6">
              <div className="grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                {/* Teléfono */}
                <div className="sm:col-span-3 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                  <label htmlFor="phoneNumber" className="block text-sm font-medium text-content-2">
                    Número de WhatsApp <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1 flex">
                    <div className="relative">
                      <select
                        value={selectedCountry}
                        onChange={(e) => handleCountryChange(e.target.value)}
                        className="appearance-none h-full rounded-l-md border block pl-3 pr-8 py-2 border-line focus:outline-none focus:ring-accent focus:border-blue-500 sm:text-sm"
                      >
                        {COUNTRIES.map(c => (
                          <option key={c.code} value={c.code}>
                            {c.flag} {c.prefix}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-content-2">
                        <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <input
                      type="tel"
                      name="phoneNumber"
                      id="phoneNumber"
                      required
                      value={phoneNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= natDigits) {
                          setPhoneNumber(value);
                        }
                      }}
                      className="flex-1 min-w-0 block w-full px-3 py-2 rounded-r-md focus:ring-accent focus:border-blue-500 sm:text-sm border-line"
                      placeholder={`${natDigits} dígitos`}
                    />
                  </div>
                  {phoneNumber && !validatePhoneNumber(phoneNumber) && (
                    <p className="mt-1 text-sm text-red-600 dark:text-red-400">
                      El número de {country.name} debe tener exactamente {natDigits} dígitos
                    </p>
                  )}
                </div>

                {/* Nombre */}
                <div className="sm:col-span-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                  <label htmlFor="visitorName" className="block text-sm font-medium text-content-2">
                    Nombre <span className="text-red-500">*</span>
                  </label>
                  <div className="mt-1">
                    <input
                      type="text"
                      name="visitorName"
                      id="visitorName"
                      required
                      value={visitorName}
                      onChange={(e) => setVisitorName(e.target.value)}
                      className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
                    />
                  </div>
                </div>

                {/* ¿Quién lo recomendó? (solo con referidos encendidos) */}
                {pideReferido && (
                  <div className="sm:col-span-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                    <label htmlFor="referido" className="block text-sm font-medium text-content-2">
                      ¿Quién lo recomendó? <span className="text-content-3 font-normal">(opcional)</span>
                    </label>
                    <div className="mt-1">
                      <input type="text" name="referido" id="referido" data-testid="alta-referido"
                        value={referido} onChange={(e) => setReferido(e.target.value)} maxLength={20}
                        placeholder="Teléfono de 10 dígitos o código de quien lo recomendó"
                        className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md" />
                    </div>
                  </div>
                )}

                {/* Email (condicional) */}
                {showEmail && (
                  <div className="sm:col-span-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                    <label htmlFor="email" className="block text-sm font-medium text-content-2">
                      Correo Electrónico
                    </label>
                    <div className="mt-1">
                      <input
                        type="email"
                        name="email"
                        id="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
                      />
                    </div>
                  </div>
                )}

                {/* Género (condicional) */}
                {showGender && (
                  <div className="sm:col-span-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                    <label htmlFor="gender" className="block text-sm font-medium text-content-2">
                      Género
                    </label>
                    <div className="mt-1">
                      <select
                        id="gender"
                        name="gender"
                        value={gender}
                        onChange={(e) => setGender(e.target.value)}
                        className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
                      >
                        <option value="">Seleccionar...</option>
                        <option value="male">Masculino</option>
                        <option value="female">Femenino</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Cumpleaños (condicional) */}
                {showBirthday && (
                  <>
                    <div className="sm:col-span-3 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                      <label htmlFor="birthDay" className="block text-sm font-medium text-content-2">
                        Día de Nacimiento
                      </label>
                      <div className="mt-1">
                        <select
                          id="birthDay"
                          name="birthDay"
                          value={birthDay}
                          onChange={(e) => setBirthDay(e.target.value)}
                          className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
                        >
                          <option value="">Día...</option>
                          {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                            <option key={day} value={day}>
                              {day}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="sm:col-span-3 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                      <label htmlFor="birthMonth" className="block text-sm font-medium text-content-2">
                        Mes de Nacimiento
                      </label>
                      <div className="mt-1">
                        <select
                          id="birthMonth"
                          name="birthMonth"
                          value={birthMonth}
                          onChange={(e) => setBirthMonth(e.target.value)}
                          className="shadow-sm focus:ring-accent focus:border-blue-500 block w-full sm:text-sm border-line rounded-md"
                        >
                          <option value="">Mes...</option>
                          {/* ✅ CORREGIDO: value ahora usa index (0-11) en lugar de index + 1 */}
                          {MONTHS.map((month, index) => (
                            <option key={month} value={index}>
                              {month}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </>
                )}

                {/* ✅ PRIMERA COMPRA (check-in real con % automático) O SALDO DE REGALO */}
                <div className="sm:col-span-6 animate-fade-in-up" style={{animationDelay: '0.15s'}}>
                  <div className="flex rounded-md border border-line overflow-hidden text-xs w-fit mb-2">
                    <button type="button" onClick={() => setInitialMode('purchase')}
                      className={`px-3 py-1.5 font-medium transition-colors ${initialMode === 'purchase' ? 'bg-accent/15 text-accent' : 'bg-surface text-content-2 hover:bg-surface-2'}`}>
                      {loyaltyMode === 'cashback' ? 'Primera compra' : 'Primera visita'}
                    </button>
                    <button type="button" onClick={() => setInitialMode('gift')}
                      className={`px-3 py-1.5 font-medium transition-colors ${initialMode === 'gift' ? 'bg-accent/15 text-accent' : 'bg-surface text-content-2 hover:bg-surface-2'}`}>
                      {loyaltyMode === 'cashback' ? 'Saldo de regalo' : 'Puntos de regalo'}
                    </button>
                  </div>
                  <label htmlFor="initialPoints" className="block text-sm font-medium text-content-2">
                     {initialMode === 'purchase'
                        ? (loyaltyMode === 'cashback' ? 'Monto de su primera compra ($)' : 'Registrar su primera visita')
                        : (loyaltyMode === 'cashback' ? 'Saldo Inicial de Regalo ($)' : 'Puntos Iniciales de Regalo')}
                  </label>
                  {!(initialMode === 'purchase' && loyaltyMode === 'visits') && (
                  <div className="mt-1 relative rounded-md shadow-sm">
                    {/* Icono condicional */}
                    <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                        {loyaltyMode === 'cashback' ? (
                             <DollarSign className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden="true" />
                        ) : (
                             <Star className="h-4 w-4 text-yellow-500" aria-hidden="true" />
                        )}
                    </div>

                    <input
                      type="number"
                      name="initialPoints"
                      id="initialPoints"
                      min="0"
                      step={loyaltyMode === 'cashback' ? "0.01" : "1"}
                      value={initialPoints}
                      onChange={(e) => {
                          const val = e.target.value;
                          if (loyaltyMode === 'visits') {
                              // Si es visitas, solo permitimos enteros
                              setInitialPoints(val.replace(/[.,]/g, '')); 
                          } else {
                              // Si es cashback, permitimos valor tal cual
                              setInitialPoints(val);
                          }
                      }}
                      className="block w-full pl-10 rounded-md border-line focus:border-blue-500 focus:ring-accent sm:text-sm"
                      placeholder={loyaltyMode === 'cashback' ? "0.00" : "0"}
                    />
                  </div>
                  )}
                  {initialMode === 'purchase' && loyaltyMode === 'cashback' && parseFloat(initialPoints) > 0 && (
                    <p className="mt-1 text-xs text-green-600 dark:text-green-400">
                      Cashback {currentUser?.cashbackPercentage || 10}%: se le abonarán <b>${(Math.round((parseFloat(initialPoints) || 0) * (currentUser?.cashbackPercentage || 10)) / 100).toFixed(2)}</b> y se registrará su visita.
                    </p>
                  )}
                  {pideFolio && (
                    <div className="mt-3">
                      <label htmlFor="folio" className="mb-1 flex items-center gap-1 text-xs font-medium text-content-2">
                        <Receipt size={12} /> Folio del ticket{modoFolio === 'required' ? '' : ' (opcional)'}
                      </label>
                      <input id="folio" name="folio" value={folio} onChange={(e) => setFolio(e.target.value)} maxLength={40}
                        data-testid="alta-folio" placeholder="Ej. 104523"
                        className="block w-full max-w-[260px] rounded-md border-line focus:border-blue-500 focus:ring-accent sm:text-sm" />
                    </div>
                  )}
                  <p className="mt-1 text-xs text-content-3">
                      {initialMode === 'purchase'
                        ? (loyaltyMode === 'cashback'
                            ? 'Teclea lo que compró: el sistema calcula su cashback solo, registra la visita y le manda su bienvenida con el saldo.'
                            : 'Se registrará su primera visita con los puntos del programa y le llegará su bienvenida.')
                        : (loyaltyMode === 'cashback'
                            ? 'Opcional. Cantidad en dinero directa (sin venta); no cuenta como visita.'
                            : 'Opcional. Puntos directos (sin venta); no cuenta como visita.')}
                  </p>
                </div>
              </div>
            </div>

            <div className="px-4 py-4 sm:px-6 bg-surface-2">
              <div className="flex justify-end">
                <button
                  type="submit"
                  disabled={loading || (phoneNumber.length !== natDigits)}
                  className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-accent disabled:opacity-50 disabled:cursor-not-allowed animate-fade-in-up"
                  style={{animationDelay: '0.15s'}}
                >
                  <Save className="h-4 w-4 mr-2" />
                  {loading ? 'Registrando...' : 'Registrar Cliente'}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </Layout>
  );
};

export default RegisterClientPage;